import { supabase } from './supabase';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/** Error de una llamada a la API, con el código/mensaje que entregó el backend. */
export class ApiError extends Error {
  readonly status: number;
  /** Código de error del backend (campo `error`), si vino. */
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * `fetch` autenticado contra la API de AMBEC. Adjunta el access token vigente,
 * serializa el body a JSON y devuelve el JSON parseado. Ante respuestas no-ok
 * lanza `ApiError` con el `error`/`message` del backend.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers(options.headers);
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  } catch {
    throw new ApiError('No se pudo conectar con el servidor.', 0);
  }

  // 204 / cuerpo vacío: no hay JSON que parsear.
  const texto = await res.text();
  const data = texto ? (JSON.parse(texto) as unknown) : null;

  if (!res.ok) {
    const cuerpo = (data ?? {}) as { error?: string; message?: string };
    const mensaje = cuerpo.message ?? cuerpo.error ?? `Error ${res.status}.`;
    throw new ApiError(mensaje, res.status, cuerpo.error);
  }

  return data as T;
}

/** Sube un archivo binario crudo (drag & drop / input file) a la API autenticado. */
export async function subirBinario<T = unknown>(path: string, archivo: File): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: archivo,
  });
  const texto = await res.text();
  const data = texto ? (JSON.parse(texto) as unknown) : null;
  if (!res.ok) {
    const cuerpo = (data ?? {}) as { error?: string; message?: string };
    throw new ApiError(cuerpo.message ?? cuerpo.error ?? `Error ${res.status}.`, res.status, cuerpo.error);
  }
  return data as T;
}

/**
 * Trae un binario autenticado y devuelve una object URL (para previsualizar el PDF real
 * en un iframe con el visor nativo). El llamador debe revocarla al cerrar.
 */
export async function obtenerBlobUrl(path: string): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(cuerpo.message ?? cuerpo.error ?? `Error ${res.status}.`, res.status, cuerpo.error);
  }
  return URL.createObjectURL(await res.blob());
}

/** Descarga un archivo binario autenticado de la API y dispara el guardado en el navegador. */
/** Descarga un archivo devuelto por un POST con cuerpo JSON (p. ej. un Word generado al vuelo). */
export async function descargarArchivoPost(path: string, body: unknown, nombre: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(cuerpo.message ?? cuerpo.error ?? `Error ${res.status}.`, res.status, cuerpo.error);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const delServidor = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/)?.[1];
  if (delServidor) nombre = decodeURIComponent(delServidor);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function descargarArchivo(path: string, nombre: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(cuerpo.message ?? cuerpo.error ?? `Error ${res.status}.`, res.status, cuerpo.error);
  }
  // El nombre canónico lo define el servidor (Content-Disposition); el parámetro
  // `nombre` queda como respaldo si el header no viene.
  const disposition = res.headers.get('content-disposition') ?? '';
  const delServidor = disposition.match(/filename="?([^";]+)"?/)?.[1];
  if (delServidor) nombre = delServidor;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
